import { observable, action, computed } from "mobx";
import { localizationService, redirect } from "@state";
import { getRedirectOriginUrl } from "@utils/specific/capacitor/helpers";
import LoaderStore from "@state/stores/common/LoaderStore.js";
import { DeviceDetector, getCurrentCulture } from "@utils";
import { BetSlipInteractionTypeEnum } from "@data-types/analytics-tracker/BetSlipInteractionTypeEnum";
import { SocialMediaShareTypeEnum } from "@data-types/analytics-tracker/SocialMediaShareTypeEnum";
import ModalService from "@services/modals/ModalService";
import { RootStore } from "@state/stores/types.js";
import { Bet, BetSlipShare } from "@models";
import { analyticsTracker } from "@utils/analytics";
import { SharedBetSlipsApiService } from "@api-services/shared-bet-slips";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";

class ShareBetSlipStore {
	rootStore: RootStore;
	loader: LoaderStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	@observable isReuseShareModalOpen = false;

	@observable isActive: boolean = false;

	@observable activeSharedBetSlip: Bet | null = null;

	@computed get isLoadingProcess(): boolean {
		return this.loader.isLoadingProcess;
	}

	@action.bound
	showPopup(sharedBetData: Bet) {
		this.activeSharedBetSlip = sharedBetData;
		this.isActive = true;

		analyticsTracker.trackBetSlipInteraction(
			BetSlipInteractionTypeEnum.Share
		);
		ModalService.onOpen();
	}

	@action.bound
	hidePopup() {
		this.activeSharedBetSlip = null;
		this.isActive = false;
	}

	@action.bound
	onBetDetailsShare(sharedBetData: Bet) {
		this.activeSharedBetSlip = sharedBetData;
		analyticsTracker.trackBetSlipInteraction(
			BetSlipInteractionTypeEnum.Share
		);
	}

	@action.bound
	onBetDetailsShareClose() {
		this.activeSharedBetSlip = null;
	}

	@action.bound
	onDispose() {
		this.activeSharedBetSlip = null;
		this.isActive = false;
		this.isReuseShareModalOpen = false;
	}

	@action.bound
	reuseSharedBetSlip(isLive: boolean, bet: BetSlipShare) {
		this.rootStore.betSlipStore.useBetSlip(bet, undefined, {
			trackAsReuse: true,
		});
		this.closeReuseBetSlipModal();

		if (DeviceDetector.isMobileTheme) {
			redirect(`/${getCurrentCulture()}/bet-slip`);
			return;
		}
		if (isLive) {
			redirect(`/${getCurrentCulture()}/${DefaultPath.PREMATCH}`);
		} else {
			redirect(`/${getCurrentCulture()}/sports/full`);
		}
	}

	/**
	 * Opens reuse bet slip modal
	 * @param {{id, betSlipNumber}} modalData
	 */
	@action.bound
	openReuseBetSlipModal() {
		this.isReuseShareModalOpen = true;
	}

	/**
	 * Closes reuse bet slip modal
	 */
	@action.bound
	closeReuseBetSlipModal() {
		this.isReuseShareModalOpen = false;
	}

	async insertSharedBetSlip() {
		if (this.activeSharedBetSlip?.id) {
			analyticsTracker.trackSocialMediaShared(
				SocialMediaShareTypeEnum.Other,
				this.activeSharedBetSlip.id
			);

			try {
				await SharedBetSlipsApiService.insertSharedBetSlip(
					this.activeSharedBetSlip.id,
					2
				);
			} catch (error) {
				console.error("Failed to insert shared bet slip");
			}
		}
	}

	async copyLink() {
		try {
			const el = document.createElement("textarea");
			el.value = `${getRedirectOriginUrl(
				`/bet-slip-shared/${this.activeSharedBetSlip?.betSlipNumber}`
			)}`;

			await this.insertSharedBetSlip();

			el.setAttribute("readonly", "");
			el.style.position = "absolute";
			el.style.left = "-9999px";
			document.body.appendChild(el);
			el.select();
			document.execCommand("copy");
			if (document.body.contains(el)) {
				document.body.removeChild(el);
			} else {
				console.warn(
					"Element not found in the body, unable to remove."
				);
			}

			this.rootStore.notificationStore.success(
				localizationService.t("mybets:SHARE.COPIED_LINK")
			);
		} catch (err) {
			console.error(err);
		}
	}
}

export default ShareBetSlipStore;
